import type { RunnerPhase } from '@sprinters/shared';

export interface SpriteState {
  /** Abscisse du coureur (px). */
  x: number;
  /** Ordonnée du sol sous ses pieds (px). */
  groundY: number;
  color: string;
  /** Distance parcourue (m) : pilote le cycle de foulée. */
  distanceM: number;
  speed: number;
  phase: RunnerPhase;
  /** Horloge d'animation (ms) pour les effets indépendants de la course (étoiles, bras levés). */
  clockMs: number;
}

const OUTLINE = 'rgba(8, 10, 18, 0.65)';
const SKIN = '#ffd9b3';
/** Distance (m) pour un cycle complet de foulée. */
const STRIDE_LENGTH_M = 1.7;

type Point = readonly [number, number];

function polyline(ctx: CanvasRenderingContext2D, ...points: Point[]): void {
  const [first, ...rest] = points;
  if (!first) return;
  ctx.beginPath();
  ctx.moveTo(first[0], first[1]);
  for (const [px, py] of rest) ctx.lineTo(px, py);
  ctx.stroke();
}

function limb(from: Point, length: number, angle: number): Point {
  return [from[0] + length * Math.sin(angle), from[1] + length * Math.cos(angle)];
}

function drawStanding(ctx: CanvasRenderingContext2D, s: SpriteState): void {
  const { x, groundY: g } = s;
  const running = s.phase === 'running' && s.speed > 0.4;
  const finished = s.phase === 'finished';
  const cycle = (s.distanceM * Math.PI * 2) / STRIDE_LENGTH_M;
  const amplitude = running ? Math.min(1, 0.35 + s.speed / 10) : 0.06;
  const lean = running ? Math.min(8, s.speed * 0.9) : 0;

  const hip: Point = [x, g - 22];
  const shoulder: Point = [x + lean * 0.6, g - 40];
  const head: Point = [x + lean, g - 49];

  const drawBody = (width: number, style: string): void => {
    ctx.lineWidth = width;
    ctx.strokeStyle = style;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    polyline(ctx, hip, shoulder);
    for (const side of [0, 1]) {
      const phi = cycle + side * Math.PI;
      const thigh = Math.sin(phi) * 0.9 * amplitude;
      const knee = limb(hip, 11, thigh);
      const foot = limb(knee, 11, thigh - (0.7 + 0.8 * Math.max(0, Math.cos(phi))) * amplitude);
      polyline(ctx, hip, knee, foot);

      if (finished) {
        const hand: Point = [
          shoulder[0] + (side === 0 ? -9 : 9),
          shoulder[1] - 15 + Math.sin(s.clockMs / 120 + side) * 2,
        ];
        polyline(ctx, shoulder, hand);
      } else {
        const upper = Math.sin(phi + Math.PI) * 0.9 * amplitude;
        const elbow = limb(shoulder, 9, upper);
        const hand = limb(elbow, 8, upper + (running ? 1.3 : 0.3));
        polyline(ctx, shoulder, elbow, hand);
      }
    }
  };

  drawBody(7, OUTLINE);
  drawBody(4, s.color);

  ctx.fillStyle = SKIN;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(head[0], head[1], 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
}

function drawFallen(ctx: CanvasRenderingContext2D, s: SpriteState): void {
  const { x, groundY: g } = s;
  const hip: Point = [x - 16, g - 7];
  const shoulder: Point = [x + 8, g - 7];

  const drawBody = (width: number, style: string): void => {
    ctx.lineWidth = width;
    ctx.strokeStyle = style;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    polyline(ctx, hip, shoulder);
    polyline(ctx, hip, [x - 30, g - 4]);
    polyline(ctx, hip, [x - 27, g - 12]);
    polyline(ctx, shoulder, [x + 16, g - 16]);
    polyline(ctx, shoulder, [x + 20, g - 3]);
  };
  drawBody(7, OUTLINE);
  drawBody(4, s.color);

  const head: Point = [x + 15, g - 10];
  ctx.fillStyle = SKIN;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(head[0], head[1], 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Étoiles « étourdi » qui tournent autour de la tête.
  ctx.fillStyle = '#ffe14d';
  for (let i = 0; i < 3; i++) {
    const angle = s.clockMs / 180 + (i * Math.PI * 2) / 3;
    ctx.beginPath();
    ctx.arc(
      head[0] + Math.cos(angle) * 13,
      head[1] - 12 + Math.sin(angle) * 4,
      2.5,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }
}

export function drawRunner(ctx: CanvasRenderingContext2D, s: SpriteState): void {
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.28)';
  ctx.beginPath();
  ctx.ellipse(s.x, s.groundY + 2, s.phase === 'fallen' ? 26 : 15, 4, 0, 0, Math.PI * 2);
  ctx.fill();

  if (s.phase === 'fallen') drawFallen(ctx, s);
  else drawStanding(ctx, s);
  ctx.restore();
}
